import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../utils/mobileUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Who can do what (matches backend/routes/fire_extinguishers.py)
const ADMIN_ROLES = ['super_admin', 'admin', 'senior_manager', 'manager'];
const ISSUE_ROLES = [...ADMIN_ROLES, 'assistant_manager', 'team', 'workshop'];
const COST_VIEW_ROLES = [...ADMIN_ROLES, 'finance', 'ceo'];

const SIZES = ['1kg', '2kg', '3kg', '4kg', '6kg', '9kg', '50kg'];
const CONDITIONS = [
  { value: 'expired', label: 'Expired' },
  { value: 'ok', label: 'OK (still in date)' },
  { value: 'discharged', label: 'Discharged / used' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing', label: 'Missing (nothing to take off)' }
];

const VEHICLE_STATUS = {
  not_verified: { label: 'Not yet verified', color: '#64748B', bg: '#F1F5F9' },
  no_fe: { label: 'No FE fitted', color: '#B91C1C', bg: '#FEE2E2' },
  expired: { label: 'Expired', color: '#B91C1C', bg: '#FEE2E2' },
  due_soon: { label: 'Due soon', color: '#B45309', bg: '#FEF3C7' },
  ok: { label: 'OK', color: '#15803D', bg: '#DCFCE7' },
  unknown: { label: 'Expiry unknown', color: '#B45309', bg: '#FEF3C7' }
};

const UNIT_STATUS = {
  in_stock: { label: 'In stock', color: '#1D4ED8', bg: '#DBEAFE' },
  fitted: { label: 'Fitted', color: '#15803D', bg: '#DCFCE7' },
  returned: { label: 'Returned – to send to vendor', color: '#B45309', bg: '#FEF3C7' },
  at_vendor: { label: 'At vendor', color: '#7C3AED', bg: '#EDE9FE' },
  condemned: { label: 'Condemned', color: '#64748B', bg: '#F1F5F9' },
  lost: { label: 'Lost', color: '#B91C1C', bg: '#FEE2E2' }
};

const today = () => new Date().toISOString().slice(0, 10);

const fmtDate = (d) => {
  if (!d) return '–';
  const [y, m, day] = d.slice(0, 10).split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${months[parseInt(m, 10) - 1]} ${y}`;
};

const addMonths = (iso, months) => {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

function Pill({ map, value }) {
  const s = map[value] || { label: value, color: '#334155', bg: '#F1F5F9' };
  return (
    <span style={{ display: 'inline-block', padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.78rem',
      fontWeight: 600, color: s.color, background: s.bg, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function errorText(data, fallback) {
  if (!data) return fallback;
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (d && d.errors) return `${d.message}: ` + d.errors.map(e => `row ${e.row} (${e.serial || 'no serial'}) – ${e.error}`).join('; ');
  return fallback;
}

function FireExtinguishers({ token, user }) {
  const isMobile = useIsMobile();
  const canManage = ADMIN_ROLES.includes(user?.role);
  const canIssue = ISSUE_ROLES.includes(user?.role);
  const canSeeCosts = COST_VIEW_ROLES.includes(user?.role);

  const [activeTab, setActiveTab] = useState('vehicles');
  const [message, setMessage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({ validity_months: 12, due_soon_days: 30, min_stock: {} });

  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, options);
    let data = null;
    try { data = await res.json(); } catch (e) { /* empty body */ }
    if (!res.ok) throw new Error(errorText(data, `Request failed (${res.status})`));
    return data;
  }, []);

  const refreshSummary = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([
        api('/api/fe/summary', { headers }),
        api('/api/fe/settings', { headers })
      ]);
      setSummary(s);
      setSettings(st);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [token]);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  const tabs = [
    { id: 'vehicles', label: 'Vehicles', icon: '🚛' },
    ...(canIssue ? [{ id: 'exchange', label: 'Record Exchange', icon: '🔄' }] : []),
    { id: 'stock', label: 'Stock', icon: '📦' },
    { id: 'register', label: 'Register', icon: '📋' },
    { id: 'reports', label: 'Reports', icon: '📥' },
    ...(canSeeCosts ? [{ id: 'vendors', label: 'Vendors & Costs', icon: '🧾' }] : []),
    ...(canManage ? [{ id: 'reminders', label: 'Reminders', icon: '🔔' }] : [])
  ];

  const byStatus = summary?.vehicles_by_status || {};

  return (
    <div data-testid="fire-extinguishers-page">
      <div className="page-header">
        <h2>🧯 Fire Extinguishers</h2>
        <p>Every extinguisher tracked by serial – what is on each vehicle, when it expires, and stock on hand</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }} data-testid="fe-message">
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* On phones the summary is hidden while recording an exchange so the form is at the top */}
      {summary && !(isMobile && activeTab === 'exchange') && (
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <div className="stat-label">Vehicles verified</div>
            <div className="stat-value">{summary.verified} / {summary.vehicles_total}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Expired or no FE</div>
            <div className="stat-value" style={{ color: '#B91C1C' }}>{(byStatus.expired || 0) + (byStatus.no_fe || 0)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Due within {summary.due_soon_days} days</div>
            <div className="stat-value" style={{ color: '#B45309' }}>{byStatus.due_soon || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">In stock</div>
            <div className="stat-value" style={{ color: summary.sizes_below_min.length ? '#B91C1C' : undefined }}>{summary.in_stock}</div>
            {summary.sizes_below_min.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#B91C1C' }}>Below minimum: {summary.sizes_below_min.join(', ')}</div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-label">Returned / at vendor</div>
            <div className="stat-value">{summary.returned} / {summary.at_vendor}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: isMobile ? '0.25rem' : '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #E2E8F0', flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`fe-tab-${tab.id}`}
            style={{
              padding: isMobile ? '0.6rem 0.8rem' : '0.75rem 1.25rem',
              background: activeTab === tab.id ? 'white' : 'transparent',
              border: activeTab === tab.id ? '2px solid #E2E8F0' : 'none',
              borderBottom: activeTab === tab.id ? '2px solid white' : 'none',
              marginBottom: '-2px',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontWeight: activeTab === tab.id ? '600' : '400',
              color: activeTab === tab.id ? '#003366' : '#64748B'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'vehicles' && (
        <VehiclesTab api={api} headers={headers} isMobile={isMobile} />
      )}
      {activeTab === 'exchange' && canIssue && (
        <ExchangeTab api={api} headers={headers} jsonHeaders={jsonHeaders} isMobile={isMobile}
          setMessage={setMessage} onDone={refreshSummary} />
      )}
      {activeTab === 'stock' && (
        <StockTab api={api} headers={headers} jsonHeaders={jsonHeaders} isMobile={isMobile} canManage={canManage}
          settings={settings} setMessage={setMessage} onDone={refreshSummary} />
      )}
      {activeTab === 'register' && (
        <RegisterTab api={api} headers={headers} jsonHeaders={jsonHeaders} canManage={canManage}
          setMessage={setMessage} onDone={refreshSummary} />
      )}
      {activeTab === 'reports' && (
        <ReportsTab headers={headers} setMessage={setMessage} canSeeCosts={canSeeCosts} />
      )}
      {activeTab === 'vendors' && canSeeCosts && (
        <VendorsTab api={api} headers={headers} jsonHeaders={jsonHeaders} canManage={canManage}
          validityMonths={settings.validity_months || 12} setMessage={setMessage} onDone={refreshSummary} />
      )}
      {activeTab === 'reminders' && canManage && (
        <RemindersTab api={api} headers={headers} jsonHeaders={jsonHeaders} setMessage={setMessage} />
      )}
    </div>
  );
}

// A kept photo, loaded with the user's login (photos are not public); tap to open full size
function PhotoThumb({ id, headers }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    fetch(`${BACKEND_URL}/api/fe/photos/${id}`, { headers })
      .then(res => (res.ok ? res.blob() : null))
      .then(blob => { if (blob) { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl); } })
      .catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);
  if (!url) return <div style={{ width: 96, height: 96, background: '#F1F5F9', borderRadius: 8 }} />;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Exchange photo" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #E2E8F0' }} />
    </a>
  );
}

// ===== VEHICLES =====

function VehiclesTab({ api, headers, isMobile }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/fe/vehicles', { headers }).then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  const openVehicle = async (vno) => {
    try {
      setSelected(await api(`/api/fe/vehicles/${encodeURIComponent(vno)}`, { headers }));
    } catch (err) {
      setSelected(null);
    }
  };

  const counts = rows.reduce((acc, r) => ({ ...acc, [r.fe_status]: (acc[r.fe_status] || 0) + 1 }), {});
  const shown = rows.filter(r => (!filter || r.fe_status === filter) &&
    (!search || r.vehicle_no.toUpperCase().includes(search.toUpperCase()) ||
      r.units.some(u => (u.serial || '').includes(search.toUpperCase()))));

  return (
    <div className="card">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <button className={`btn btn-sm ${filter === '' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('')}>
          All ({rows.length})
        </button>
        {['expired', 'no_fe', 'due_soon', 'ok', 'not_verified'].map(s => (
          <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(s)} data-testid={`fe-filter-${s}`}>
            {VEHICLE_STATUS[s].label} ({counts[s] || 0})
          </button>
        ))}
      </div>
      <input className="form-input" placeholder="Search vehicle no. or serial…" value={search}
        onChange={e => setSearch(e.target.value)} style={{ marginBottom: '1rem', maxWidth: '360px' }} />

      {loading ? <p className="text-muted">Loading…</p> : shown.length === 0 ? (
        <div className="empty-state"><p>No vehicles match.</p></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" data-testid="fe-vehicles-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                {!isMobile && <th>Dept / Project</th>}
                <th>FE status</th>
                <th>On board</th>
                {!isMobile && <th>Earliest expiry</th>}
                {!isMobile && <th>Last exchange</th>}
              </tr>
            </thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.vehicle_no} onClick={() => openVehicle(r.vehicle_no)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600 }}>{r.vehicle_no}</td>
                  {!isMobile && <td>{[r.department, r.project].filter(Boolean).join(' / ') || '–'}</td>}
                  <td><Pill map={VEHICLE_STATUS} value={r.fe_status} /></td>
                  <td>
                    {r.units.length === 0 ? '–' : r.units.map(u => (
                      <div key={u.id} style={{ fontSize: '0.85rem' }}>
                        <span className="font-mono">{u.serial || '(no serial)'}</span> · {u.size}
                        {isMobile && <> · exp {fmtDate(u.expiry_date)}</>}
                      </div>
                    ))}
                  </td>
                  {!isMobile && <td>{fmtDate(r.earliest_expiry)}{r.days_to_expiry != null && (
                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                      {r.days_to_expiry < 0 ? `${-r.days_to_expiry} days ago` : `in ${r.days_to_expiry} days`}
                    </div>)}</td>}
                  {!isMobile && <td>{fmtDate(r.last_exchange)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }} data-testid="fe-vehicle-modal">
            <div className="modal-header">
              <h3 className="modal-title">{selected.vehicle_no} <Pill map={VEHICLE_STATUS} value={selected.fe_status} /></h3>
              <button className="modal-close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-muted">{[selected.department, selected.project].filter(Boolean).join(' / ')}</p>
              <h4>On board now ({selected.unit_count})</h4>
              {selected.units.length === 0 ? <p className="text-muted">None recorded.</p> : (
                <table className="table">
                  <thead><tr><th>Serial</th><th>Size</th><th>Fitted</th><th>Expiry</th></tr></thead>
                  <tbody>
                    {selected.units.map(u => (
                      <tr key={u.id}>
                        <td className="font-mono">{u.serial || '(no serial)'}</td><td>{u.size}</td>
                        <td>{fmtDate(u.fitted_date)}</td><td>{fmtDate(u.expiry_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <h4 style={{ marginTop: '1rem' }}>History</h4>
              {selected.history.length === 0 ? <p className="text-muted">No exchanges recorded yet.</p> : (
                <table className="table">
                  <thead><tr><th>Date</th><th>Action</th><th>Serial</th><th>Size</th><th>By</th></tr></thead>
                  <tbody>
                    {selected.history.map(m => (
                      <tr key={m.id}>
                        <td>{fmtDate(m.date)}</td>
                        <td>{m.type === 'fit' ? 'Fitted' : `Taken off (${m.condition})`}</td>
                        <td className="font-mono">{m.serial || '(no serial)'}</td>
                        <td>{m.size}</td>
                        <td>{m.by?.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(() => {
                const ids = [...new Set(selected.history.flatMap(m => m.photo_ids || []))];
                return ids.length > 0 && (
                  <>
                    <h4 style={{ marginTop: '1rem' }}>Photos ({ids.length})</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }} data-testid="fe-vehicle-photos">
                      {ids.map(id => <PhotoThumb key={id} id={id} headers={headers} />)}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== EXCHANGE =====

// fromPhoto: the number filled in from a photo, so a different typed number can be queried before saving
const emptyRemoved = () => ({ serial: '', cylinder_label: '', size: '9kg', expiry_date: '', condition: 'expired', notes: '', read: null, fromPhoto: null });
const emptyFit = () => ({ serial: '', label: '', read: null, fromPhoto: null });

// Shrink a phone photo before sending: faster on mobile data, cheaper to read, small enough to keep as proof
function shrinkPhoto(file, maxSide = 1600, quality = 0.8) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// 📷 button: opens the back camera, sends the photo to be read, and hands back what was read
function PhotoButton({ api, headers, kind, purpose, vehicleNo, onResult, testId }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', await shrinkPhoto(file), 'photo.jpg');
      form.append('kind', kind);
      if (purpose) form.append('purpose', purpose);
      if (vehicleNo) form.append('vehicle_no', vehicleNo);
      onResult(await api('/api/fe/photo/read', { method: 'POST', headers, body: form }));
    } catch (err) {
      onResult({ error: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        onChange={onFile} data-testid={testId ? `${testId}-file` : undefined} />
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => inputRef.current.click()} disabled={busy}
        title="Take a photo" data-testid={testId} style={{ whiteSpace: 'nowrap' }}>
        {busy ? 'Reading…' : '📷'}
      </button>
    </>
  );
}

// What the photo said: OK, a problem, or "did you mean" choices
function ReadNote({ read, onPick }) {
  if (!read) return null;
  if (read.error) return <div className="text-error" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>{read.error}</div>;
  const ok = !read.message;
  return (
    <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }} data-testid="fe-read-note">
      <span className={ok ? 'text-success' : 'text-warning'}>
        📷 {ok ? 'Read from photo – please check it matches.' : read.message}
      </span>
      {read.suggestions && read.suggestions.length > 0 && (
        <span> Did you mean{' '}
          {read.suggestions.map(s => (
            <button key={s} type="button" className="btn btn-sm btn-ghost" style={{ padding: '0 0.4rem' }} onClick={() => onPick(s)}>{s}</button>
          ))}?
        </span>
      )}
    </div>
  );
}

function ExchangeTab({ api, headers, jsonHeaders, isMobile, setMessage, onDone }) {
  const [vehicles, setVehicles] = useState([]);
  const [stock, setStock] = useState([]);
  const [vehicleNo, setVehicleNo] = useState('');
  const [plateRead, setPlateRead] = useState(null);
  const [plateFromPhoto, setPlateFromPhoto] = useState(null);
  const [current, setCurrent] = useState(null);
  const [date, setDate] = useState(today());
  const [fit, setFit] = useState([emptyFit()]);
  const [removed, setRemoved] = useState([]);
  const [photoIds, setPhotoIds] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStock = useCallback(() => {
    api('/api/fe/units?status=in_stock', { headers }).then(setStock).catch(() => setStock([]));
  }, []);

  useEffect(() => {
    api('/api/vehicles', { headers }).then(v => setVehicles(v.map(x => x.vehicle_no))).catch(() => {});
    loadStock();
  }, []);

  // Show what is recorded on the vehicle once a valid vehicle number is entered
  useEffect(() => {
    const vno = vehicleNo.trim().toUpperCase();
    if (!vehicles.includes(vno)) { setCurrent(null); return; }
    api(`/api/fe/vehicles/${encodeURIComponent(vno)}`, { headers }).then(setCurrent).catch(() => setCurrent(null));
  }, [vehicleNo, vehicles]);

  const stockBySerial = Object.fromEntries(stock.map(u => [u.serial, u]));
  const keepPhoto = (res) => { if (res && res.photo_id) setPhotoIds(ids => [...ids, res.photo_id]); };

  const onPlatePhoto = (res) => {
    keepPhoto(res);
    setPlateRead(res);
    const plate = (res.match && res.match.vehicle_no) || (res.read && res.read.plate);
    if (plate) { setVehicleNo(plate); setPlateFromPhoto(plate); }
  };

  const onFitPhoto = (i, res) => {
    keepPhoto(res);
    const read = res.read || {};
    const serial = (res.match && res.match.serial) || read.service_tag || '';
    setFit(f => f.map((x, j) => j === i ? { ...x, serial: serial || x.serial, label: read.cylinder_label || (res.match && res.match.cylinder_label) || x.label, read: res, fromPhoto: serial || x.fromPhoto } : x));
  };

  const onRemovedPhoto = (i, res) => {
    keepPhoto(res);
    const read = res.read || {};
    const m = res.match || {};
    setRemoved(rs => rs.map((x, j) => j === i ? {
      ...x,
      serial: m.serial || read.service_tag || x.serial,
      cylinder_label: read.cylinder_label || m.cylinder_label || x.cylinder_label,
      size: read.size || m.size || x.size,
      expiry_date: read.expiry || m.expiry_date || x.expiry_date,
      read: res,
      fromPhoto: m.serial || read.service_tag || x.fromPhoto
    } : x));
  };

  const takeOff = (u) => {
    if (removed.some(r => r.serial === u.serial)) return;
    setRemoved([...removed, { ...emptyRemoved(), serial: u.serial || '', cylinder_label: u.cylinder_label || '', size: u.size, expiry_date: u.expiry_date || '' }]);
  };

  const reset = () => {
    setVehicleNo(''); setPlateRead(null); setPlateFromPhoto(null); setCurrent(null); setDate(today()); setFit([emptyFit()]); setRemoved([]);
    setPhotoIds([]); setNotes('');
  };

  const submit = async () => {
    // A number typed over the one read from the photo is allowed, but checked first
    const differs = (typed, read) => read && typed.trim().toUpperCase() !== read.toUpperCase();
    const changed = [
      ...(differs(vehicleNo, plateFromPhoto) ? [`Vehicle: photo read ${plateFromPhoto}, saving ${vehicleNo.trim() || '(blank)'}`] : []),
      ...removed.filter(r => differs(r.serial, r.fromPhoto)).map(r => `Taken off: photo read ${r.fromPhoto}, saving ${r.serial.trim() || '(blank)'}`),
      ...fit.filter(f => differs(f.serial, f.fromPhoto)).map(f => `Fitted: photo read ${f.fromPhoto}, saving ${f.serial.trim() || '(blank)'}`)
    ];
    if (changed.length && !window.confirm(`These numbers are different from what the photos show:\n\n${changed.join('\n')}\n\nSave anyway?`)) return;
    const fitted = fit.filter(f => f.serial.trim());
    const payload = {
      vehicle_no: vehicleNo.trim(),
      date,
      fit: fitted.map(f => f.serial.trim()),
      fit_labels: Object.fromEntries(fitted.filter(f => f.label.trim()).map(f => [f.serial.trim(), f.label.trim()])),
      removed: removed.map(({ read, fromPhoto, ...r }) => ({ ...r, serial: r.serial.trim() || null, cylinder_label: r.cylinder_label.trim() || null, expiry_date: r.expiry_date || null })),
      photo_ids: photoIds,
      notes: notes || null
    };
    setSaving(true);
    try {
      const res = await api('/api/fe/exchange', { method: 'POST', headers: jsonHeaders, body: JSON.stringify(payload) });
      const photos = photoIds.length ? ` ${photoIds.length} photo(s) kept as proof.` : '';
      setMessage({ type: 'success', text: `Saved for ${res.vehicle_no}: ${res.fitted.length} fitted, ${res.removed.length} taken off. ${res.now_on_vehicle.length} unit(s) now on board.${photos}` });
      reset();
      loadStock();
      onDone();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const grid = { display: 'grid', gap: '0.5rem', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1.2fr 0.7fr 1fr 1.2fr auto', alignItems: 'end' };
  const withCamera = { display: 'flex', gap: '0.4rem', alignItems: 'center' };

  return (
    <div className="card" data-testid="fe-exchange-form">
      <h3 style={{ marginTop: 0 }}>Record an exchange</h3>
      <p className="text-muted" style={{ marginBottom: '1rem' }}>
        Tap 📷 to photograph the number plate and each extinguisher's labels – the numbers are filled in for you
        and the photos are kept as proof. You can always type instead.
      </p>

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
        <div className="form-group">
          <label className="form-label">Vehicle no.</label>
          <div style={withCamera}>
            <input className="form-input" list="fe-vehicle-list" value={vehicleNo} data-testid="fe-vehicle-input"
              onChange={e => { setVehicleNo(e.target.value.toUpperCase()); setPlateRead(null); }} placeholder="e.g. XE1234A" />
            <PhotoButton api={api} headers={headers} kind="plate" onResult={onPlatePhoto} testId="fe-photo-plate" />
          </div>
          <ReadNote read={plateRead} onPick={v => { setVehicleNo(v); setPlateFromPhoto(v); setPlateRead(null); }} />
          <datalist id="fe-vehicle-list">{vehicles.map(v => <option key={v} value={v} />)}</datalist>
        </div>
        <div className="form-group">
          <label className="form-label">Date</label>
          <input type="date" className="form-input" value={date} max={today()} onChange={e => setDate(e.target.value)} />
        </div>
      </div>

      {current && (
        <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem' }}>
          <strong>Recorded on {current.vehicle_no}:</strong>{' '}
          {current.fe_status === 'not_verified' ? 'nothing yet – first visit, record what is actually on board.' :
            current.units.length === 0 ? 'no units.' : null}
          {current.units.map(u => (
            <div key={u.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.4rem', flexWrap: 'wrap' }}>
              <span className="font-mono">{u.serial || '(no serial)'}</span> · {u.size} · exp {fmtDate(u.expiry_date)}
              <button className="btn btn-sm btn-secondary" onClick={() => takeOff(u)}>Take off</button>
            </div>
          ))}
        </div>
      )}

      <h4>Taken off the vehicle</h4>
      {removed.length === 0 && <p className="text-muted" style={{ fontSize: '0.85rem' }}>None added.</p>}
      {removed.map((r, i) => (
        <div key={i} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px dashed #E2E8F0' }}>
          <div style={grid}>
            <div>
              <label className="form-label">Service tag (RW…)</label>
              <div style={withCamera}>
                <input className="form-input" value={r.serial} placeholder="blank if none"
                  onChange={e => setRemoved(removed.map((x, j) => j === i ? { ...x, serial: e.target.value.toUpperCase(), read: null } : x))} />
                <PhotoButton api={api} headers={headers} kind="tag" purpose="remove" vehicleNo={vehicleNo}
                  onResult={res => onRemovedPhoto(i, res)} testId={`fe-photo-removed-${i}`} />
              </div>
            </div>
            <div>
              <label className="form-label">Cylinder label (PLS/SETSCO)</label>
              <input className="form-input" value={r.cylinder_label} placeholder="optional"
                onChange={e => setRemoved(removed.map((x, j) => j === i ? { ...x, cylinder_label: e.target.value.toUpperCase() } : x))} />
            </div>
            <div>
              <label className="form-label">Size</label>
              <select className="form-select" value={r.size}
                onChange={e => setRemoved(removed.map((x, j) => j === i ? { ...x, size: e.target.value } : x))}>
                {SIZES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Expiry on tag</label>
              <input type="date" className="form-input" value={r.expiry_date}
                onChange={e => setRemoved(removed.map((x, j) => j === i ? { ...x, expiry_date: e.target.value } : x))} />
            </div>
            <div>
              <label className="form-label">Condition</label>
              <select className="form-select" value={r.condition}
                onChange={e => setRemoved(removed.map((x, j) => j === i ? { ...x, condition: e.target.value } : x))}>
                {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => setRemoved(removed.filter((_, j) => j !== i))}>Remove</button>
          </div>
          <ReadNote read={r.read} onPick={s => setRemoved(removed.map((x, j) => j === i ? { ...x, serial: s, fromPhoto: s, read: null } : x))} />
        </div>
      ))}
      <button className="btn btn-sm btn-secondary" onClick={() => setRemoved([...removed, emptyRemoved()])} data-testid="fe-add-removed">
        + Unit taken off
      </button>

      <h4 style={{ marginTop: '1.5rem' }}>Fitted from stock</h4>
      {fit.map((f, i) => {
        const match = stockBySerial[f.serial.trim().toUpperCase()];
        return (
          <div key={i} style={{ marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ ...withCamera, flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                <input className="form-input" list="fe-stock-list" value={f.serial} placeholder="Service tag (RW…)"
                  style={{ maxWidth: isMobile ? 'none' : '220px' }} data-testid={`fe-fit-${i}`}
                  onChange={e => setFit(fit.map((x, j) => j === i ? { ...x, serial: e.target.value.toUpperCase(), read: null } : x))} />
                <PhotoButton api={api} headers={headers} kind="tag" purpose="fit"
                  onResult={res => onFitPhoto(i, res)} testId={`fe-photo-fit-${i}`} />
              </div>
              <input className="form-input" value={f.label} placeholder="Cylinder label (optional)"
                style={isMobile ? { flex: '1 1 100%', marginRight: 'calc(0.4rem + 44px)' } : { maxWidth: '200px' }}
                onChange={e => setFit(fit.map((x, j) => j === i ? { ...x, label: e.target.value.toUpperCase() } : x))} />
              {f.serial.trim() && (match
                ? <span className="text-success" style={{ fontSize: '0.85rem' }}>✓ {match.size} · exp {fmtDate(match.expiry_date)}</span>
                : <span className="text-error" style={{ fontSize: '0.85rem' }}>Not in stock</span>)}
              {fit.length > 1 && <button className="btn btn-sm btn-ghost" onClick={() => setFit(fit.filter((_, j) => j !== i))}>Remove</button>}
            </div>
            <ReadNote read={f.read} onPick={s => setFit(fit.map((x, j) => j === i ? { ...x, serial: s, fromPhoto: s, read: null } : x))} />
          </div>
        );
      })}
      <datalist id="fe-stock-list">{stock.map(u => <option key={u.id} value={u.serial}>{u.size} · exp {u.expiry_date}</option>)}</datalist>
      <button className="btn btn-sm btn-secondary" onClick={() => setFit([...fit, emptyFit()])}>+ Another unit</button>
      <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
        {stock.length} unit(s) in stock.{photoIds.length > 0 && ` ${photoIds.length} photo(s) will be kept with this exchange.`}
      </p>

      <div className="form-group" style={{ marginTop: '1rem' }}>
        <label className="form-label">Notes (optional)</label>
        <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={submit} disabled={saving || !vehicleNo} data-testid="fe-save-exchange">
          {saving ? 'Saving…' : 'Save exchange'}
        </button>
        <button className="btn btn-secondary" onClick={reset}>Clear</button>
      </div>
    </div>
  );
}

// ===== STOCK =====

function StockTab({ api, headers, jsonHeaders, isMobile, canManage, settings, setMessage, onDone }) {
  const [stock, setStock] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [minStock, setMinStock] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [backInStock, setBackInStock] = useState(null);
  const [picked, setPicked] = useState([]);       // returned units ticked to send to a vendor
  const [sendOpen, setSendOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api('/api/fe/stock', { headers }),
        api('/api/fe/units?status=returned,at_vendor', { headers })
      ]);
      setStock(s);
      setPipeline(p);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setMinStock(settings.min_stock || {}); }, [settings]);

  const saveMin = async () => {
    try {
      await api('/api/fe/settings', { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ min_stock: minStock }) });
      setMessage({ type: 'success', text: 'Minimum stock levels saved.' });
      load(); onDone();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  const changeStatus = async (unit, body) => {
    try {
      await api(`/api/fe/units/${unit.id}/status`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
      setBackInStock(null);
      load(); onDone();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Stock by size</h3>
          {canManage && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => setShowUpload(true)} data-testid="fe-upload-btn">⬆ Upload from Excel</button>
              <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="fe-add-stock-btn">+ Add stock</button>
            </div>
          )}
        </div>
        {!stock ? <p className="text-muted">Loading…</p> : stock.sizes.length === 0 ? (
          <div className="empty-state"><p>No extinguishers recorded yet. Use “Add stock” to load the units collected from the vendor.</p></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" data-testid="fe-stock-table">
              <thead>
                <tr>
                  <th>Size</th><th>In stock</th><th>Minimum</th>
                  <th>Fitted</th><th>Due ≤ {stock.due_soon_days}d</th><th>Expired on vehicles</th>
                  {!isMobile && <><th>Returned</th><th>At vendor</th></>}
                </tr>
              </thead>
              <tbody>
                {stock.sizes.map(r => (
                  <tr key={r.size}>
                    <td style={{ fontWeight: 600 }}>{r.size}</td>
                    <td style={{ color: r.below_min ? '#B91C1C' : undefined, fontWeight: r.below_min ? 700 : 400 }}>
                      {r.in_stock}{r.below_min && ' ⚠'}
                    </td>
                    <td>
                      {canManage ? (
                        <input type="number" min="0" className="form-input" style={{ width: '70px', padding: '0.25rem' }}
                          value={minStock[r.size] ?? 0}
                          onChange={e => setMinStock({ ...minStock, [r.size]: parseInt(e.target.value || '0', 10) })} />
                      ) : r.min_stock}
                    </td>
                    <td>{r.fitted}</td>
                    <td>{r.fitted_due_soon}</td>
                    <td style={{ color: r.fitted_expired ? '#B91C1C' : undefined }}>{r.fitted_expired}</td>
                    {!isMobile && <><td>{r.returned}</td><td>{r.at_vendor}</td></>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {canManage && stock && stock.sizes.length > 0 && (
          <button className="btn btn-sm btn-secondary" onClick={saveMin} style={{ marginTop: '0.75rem' }}>Save minimum levels</button>
        )}
        {stock && stock.sizes.some(r => r.shortfall > 0) && (
          <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
            Not enough stock to replace units expiring soon: {stock.sizes.filter(r => r.shortfall > 0).map(r => `${r.shortfall} × ${r.size}`).join(', ')} short.
            With about 2 weeks at the vendor, send returned units for revalidation now.
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Returned and at vendor ({pipeline.length})</h3>
          {canManage && (
            <button className="btn btn-primary btn-sm" disabled={picked.length === 0} onClick={() => setSendOpen(true)} data-testid="fe-send-selected">
              Send {picked.length || ''} selected to vendor
            </button>
          )}
        </div>
        {canManage && pipeline.some(u => u.status === 'at_vendor' && u.batch_id) && (
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>Units sent in a batch are received back under <strong>Vendors &amp; Costs</strong>, together with the invoice.</p>
        )}
        {pipeline.length === 0 ? <p className="text-muted">Nothing waiting.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr>
                {canManage && <th><input type="checkbox" aria-label="Select all returned"
                  checked={picked.length > 0 && picked.length === pipeline.filter(u => u.status === 'returned').length}
                  onChange={e => setPicked(e.target.checked ? pipeline.filter(u => u.status === 'returned').map(u => u.id) : [])} /></th>}
                <th>Serial</th><th>Size</th><th>Status</th><th>Old expiry</th>{canManage && <th>Action</th>}
              </tr></thead>
              <tbody>
                {pipeline.map(u => (
                  <tr key={u.id}>
                    {canManage && <td>{u.status === 'returned' && (
                      <input type="checkbox" checked={picked.includes(u.id)} data-testid={`fe-pick-${u.serial || u.id}`}
                        onChange={e => setPicked(e.target.checked ? [...picked, u.id] : picked.filter(x => x !== u.id))} />)}</td>}
                    <td className="font-mono">{u.serial || '(no serial)'}</td>
                    <td>{u.size}</td>
                    <td><Pill map={UNIT_STATUS} value={u.status} /></td>
                    <td>{fmtDate(u.expiry_date)}</td>
                    {canManage && (
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {!(u.status === 'at_vendor' && u.batch_id) && (
                            <button className="btn btn-sm btn-secondary" onClick={() => setBackInStock({ unit: u, serial: u.serial || '', date: today(), expiry_date: addMonths(today(), settings.validity_months || 12) })}>
                              Back in stock
                            </button>
                          )}
                          {!(u.status === 'at_vendor' && u.batch_id) && (
                            <button className="btn btn-sm btn-ghost" onClick={() => window.confirm('Condemn this unit? It will be taken out of use.') && changeStatus(u, { status: 'condemned', date: today() })}>
                              Condemn
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sendOpen && (
        <SendToVendorModal api={api} headers={headers} jsonHeaders={jsonHeaders}
          units={pipeline.filter(u => picked.includes(u.id))}
          onClose={() => setSendOpen(false)}
          onSaved={(b) => {
            setSendOpen(false); setPicked([]);
            setMessage({ type: 'success', text: `${b.batch_no}: ${b.unit_count} unit(s) sent to ${b.vendor_name}. Receive them back under Vendors & Costs.` });
            load(); onDone();
          }} />
      )}

      {showAdd && (
        <AddStockModal api={api} jsonHeaders={jsonHeaders} validityMonths={settings.validity_months || 12}
          onClose={() => setShowAdd(false)}
          onSaved={(n) => { setShowAdd(false); setMessage({ type: 'success', text: `${n} unit(s) added to stock.` }); load(); onDone(); }} />
      )}

      {showUpload && (
        <UploadModal api={api} headers={headers} onClose={() => setShowUpload(false)}
          onSaved={(res) => {
            setShowUpload(false);
            const skipped = res.skipped.length ? ` ${res.skipped.length} row(s) with problems were skipped – fix them and upload the same file again.` : '';
            setMessage({ type: res.skipped.length ? 'warning' : 'success', text: `Upload saved: ${res.units_added} unit(s) added to stock, ${res.units_returned} old unit(s) added to the returned pile, ${res.exchanges} exchange(s) recorded (${res.units_fitted} fitted, ${res.units_taken_off} taken off).${skipped}` });
            load(); onDone();
          }} />
      )}

      {backInStock && (
        <div className="modal-overlay" onClick={() => setBackInStock(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Back in stock</h3>
              <button className="modal-close" onClick={() => setBackInStock(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service tag (RW…) – enter the new tag if the vendor put one on</label>
                <input className="form-input" value={backInStock.serial} data-testid="fe-back-in-stock-serial"
                  onChange={e => setBackInStock({ ...backInStock, serial: e.target.value.toUpperCase() })} />
                {backInStock.unit.serial && backInStock.serial && backInStock.serial !== backInStock.unit.serial && (
                  <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    Old tag {backInStock.unit.serial} is kept in the unit's history.
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Revalidated on</label>
                <input type="date" className="form-input" value={backInStock.date}
                  onChange={e => setBackInStock({ ...backInStock, date: e.target.value, expiry_date: addMonths(e.target.value, settings.validity_months || 12) })} />
              </div>
              <div className="form-group">
                <label className="form-label">New expiry</label>
                <input type="date" className="form-input" value={backInStock.expiry_date}
                  onChange={e => setBackInStock({ ...backInStock, expiry_date: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setBackInStock(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => changeStatus(backInStock.unit, {
                status: 'in_stock', date: backInStock.date, expiry_date: backInStock.expiry_date,
                serial: backInStock.serial || null
              })}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SendToVendorModal({ api, headers, jsonHeaders, units, onClose, onSaved }) {
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({ vendor_id: '', sent_date: today(), reference: '', notes: '' });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/api/fe/vendors', { headers }).then(v => {
      const active = v.filter(x => x.active !== false);
      setVendors(active);
      if (active.length) setForm(f => ({ ...f, vendor_id: active[0].id }));
    }).catch(err => setError(err.message));
  }, []);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      onSaved(await api('/api/fe/batches', { method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ ...form, unit_ids: units.map(u => u.id) }) }));
    } catch (err) { setError(err.message); setSaving(false); }
  };

  const sizes = units.reduce((a, u) => ({ ...a, [u.size]: (a[u.size] || 0) + 1 }), {});
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }} data-testid="fe-send-modal">
        <div className="modal-header">
          <h3 className="modal-title">Send to vendor</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <p><strong>{units.length} unit(s):</strong> {Object.entries(sizes).map(([s, n]) => `${n} × ${s}`).join(', ')}</p>
          <div className="form-group">
            <label className="form-label">Vendor</label>
            <select className="form-select" value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })}>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Add vendors under Vendors &amp; Costs.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Date sent</label>
            <input type="date" className="form-input" value={form.sent_date} max={today()} onChange={e => setForm({ ...form, sent_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">DO / job reference (optional)</label>
            <input className="form-input" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !form.vendor_id} data-testid="fe-send-save">
            {saving ? 'Sending…' : `Send ${units.length} unit(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddStockModal({ api, jsonHeaders, validityMonths, onClose, onSaved }) {
  const [revalidated, setRevalidated] = useState(today());
  const [expiry, setExpiry] = useState(addMonths(today(), validityMonths));
  const [size, setSize] = useState('9kg');
  const [serials, setSerials] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // One serial per line; a line may override the size, e.g. "FEX_ZAA_123, 1kg"
  const lines = serials.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
    const [serial, s] = l.split(/[,\t]/).map(x => x.trim());
    return { serial, size: s || size };
  });

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const res = await api('/api/fe/units/bulk', {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ units: lines.map(l => ({ serial: l.serial, size: l.size, expiry_date: expiry, revalidated_date: revalidated })) })
      });
      onSaved(res.added);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }} data-testid="fe-add-stock-modal">
        <div className="modal-header">
          <h3 className="modal-title">Add stock</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Revalidated / received on</label>
              <input type="date" className="form-input" value={revalidated}
                onChange={e => { setRevalidated(e.target.value); setExpiry(addMonths(e.target.value, validityMonths)); }} />
            </div>
            <div className="form-group">
              <label className="form-label">Expiry</label>
              <input type="date" className="form-input" value={expiry} onChange={e => setExpiry(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Default size</label>
            <select className="form-select" value={size} onChange={e => setSize(e.target.value)}>
              {SIZES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Serial numbers – one per line (add “, 1kg” to a line for a different size)</label>
            <textarea className="form-textarea" rows={8} value={serials} onChange={e => setSerials(e.target.value)}
              placeholder={'RW8765519\nRW8765520\nRW8765514, 1kg'} data-testid="fe-serials-input" />
          </div>
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            {lines.length} unit(s): {Object.entries(lines.reduce((a, l) => ({ ...a, [l.size]: (a[l.size] || 0) + 1 }), {})).map(([s, n]) => `${n} × ${s}`).join(', ') || '–'}
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || lines.length === 0} data-testid="fe-save-stock">
            {saving ? 'Saving…' : lines.length === 0 ? 'Add units' : `Add ${lines.length} unit(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadModal({ api, headers, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const send = async (path) => {
    const form = new FormData();
    form.append('file', file);
    return api(path, { method: 'POST', headers, body: form });
  };

  const downloadTemplate = async () => {
    setBusy('template');
    try {
      const res = await fetch(`${BACKEND_URL}/api/fe/import/template`, { headers });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'fe_upload_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); } finally { setBusy(null); }
  };

  const runCheck = async () => {
    setBusy('check'); setError(null); setCheck(null);
    try { setCheck(await send('/api/fe/import/check')); } catch (err) { setError(err.message); } finally { setBusy(null); }
  };

  const save = async (skipErrors) => {
    if (skipErrors && !window.confirm(
      `${check.errors.length} row(s) with problems will be skipped. Everything else is saved now.\n\n` +
      'Fix the skipped rows later and upload the same file again - rows already saved are recognised and not saved twice.')) return;
    setBusy('save'); setError(null);
    try { onSaved(await send(skipErrors ? '/api/fe/import?skip_errors=true' : '/api/fe/import')); }
    catch (err) { setError(err.message); setBusy(null); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px' }} data-testid="fe-upload-modal">
        <div className="modal-header">
          <h3 className="modal-title">Upload from Excel</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <ol style={{ paddingLeft: '1.4rem', marginTop: 0, listStyle: 'decimal' }}>
            <li>
              Download the template and fill in the <strong>Stock</strong>, <strong>Returned</strong> and/or <strong>Exchanges</strong> sheet.{' '}
              <button className="btn btn-sm btn-secondary" onClick={downloadTemplate} disabled={busy === 'template'} data-testid="fe-template-btn">
                ⬇ Template
              </button>
            </li>
            <li style={{ marginTop: '0.5rem' }}>Choose the file and press <strong>Check file</strong>. Nothing is saved yet.</li>
            <li style={{ marginTop: '0.5rem' }}>If every row is OK, press <strong>Save upload</strong>.</li>
          </ol>
          <input type="file" accept=".xlsx" data-testid="fe-upload-file"
            onChange={e => { setFile(e.target.files[0] || null); setCheck(null); setError(null); }} />

          {error && <div className="alert alert-error" style={{ marginTop: '1rem' }}>{error}</div>}

          {check && (
            <div style={{ marginTop: '1rem' }} data-testid="fe-upload-result">
              <div className={`alert ${check.ok ? 'alert-success' : 'alert-error'}`}>
                {check.ok
                  ? (check.has_rows_to_save ? 'All rows are OK – ready to save.' : 'Everything in this file is already saved – nothing new to add.')
                  : `${check.errors.length} problem(s) found. Nothing has been saved yet. Fix them and check again, or save the OK rows now and upload the file again later.`}
              </div>
              <p>
                <strong>Stock:</strong> {check.stock.length} unit(s){' '}
                {Object.keys(check.stock_by_size).length > 0 && `(${Object.entries(check.stock_by_size).map(([s, n]) => `${n} × ${s}`).join(', ')})`}
                <br />
                <strong>Returned (taken off, vehicle not recorded):</strong> {check.returned.length} unit(s)
                <br />
                <strong>Exchanges:</strong> {check.exchanges.length} vehicle visit(s) – {check.units_fitted} fitted, {check.units_taken_off} taken off
                {check.already_saved.length > 0 && (
                  <><br /><span className="text-muted">Already saved by an earlier upload (will be skipped): {check.already_saved.length} row item(s)</span></>
                )}
              </p>
              {check.errors.length > 0 && (
                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <table className="table">
                    <thead><tr><th>Sheet</th><th>Row</th><th>Problem</th></tr></thead>
                    <tbody>
                      {check.errors.map((e, i) => (
                        <tr key={i}><td>{e.sheet}</td><td>{e.row}</td><td>{e.error}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {check.ok && check.exchanges.length > 0 && (
                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <table className="table">
                    <thead><tr><th>Date</th><th>Vehicle</th><th>Fitted</th><th>Taken off</th></tr></thead>
                    <tbody>
                      {check.exchanges.map((x, i) => (
                        <tr key={i}>
                          <td>{fmtDate(x.date)}</td>
                          <td>{x.vehicle_no}</td>
                          <td className="font-mono">{x.fit.map(f => `${f.serial} (${f.size})`).join(', ') || '–'}</td>
                          <td className="font-mono">{x.removed.map(r => `${r.serial || '(no tag)'} (${r.size}, ${r.condition})`).join(', ') || '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={runCheck} disabled={!file || busy === 'check'} data-testid="fe-upload-check">
            {busy === 'check' ? 'Checking…' : 'Check file'}
          </button>
          {check && !check.ok && check.has_rows_to_save ? (
            <button className="btn btn-primary" onClick={() => save(true)} disabled={busy === 'save'} data-testid="fe-upload-save-ok-rows">
              {busy === 'save' ? 'Saving…' : 'Save the OK rows only'}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => save(false)} disabled={!check?.ok || !check?.has_rows_to_save || busy === 'save'} data-testid="fe-upload-save">
              {busy === 'save' ? 'Saving…' : 'Save upload'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== REGISTER =====

function RegisterTab({ api, headers, jsonHeaders, canManage, setMessage, onDone }) {
  const [units, setUnits] = useState([]);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (status) q.set('status', status);
    if (search) q.set('search', search);
    api(`/api/fe/units?${q}`, { headers }).then(setUnits).catch(() => setUnits([]));
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    try {
      await api(`/api/fe/units/${editing.id}`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ serial: editing.serial, size: editing.size, expiry_date: editing.expiry_date, cylinder_label: editing.cylinder_label || '', notes: editing.notes || '' })
      });
      setEditing(null);
      setMessage({ type: 'success', text: 'Unit updated. The change is kept in its history.' });
      load(); onDone();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <select className="form-select" value={status} onChange={e => setStatus(e.target.value)} style={{ maxWidth: '240px' }}>
          <option value="">All statuses</option>
          {Object.entries(UNIT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input className="form-input" placeholder="Search tag, cylinder label or vehicle…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: '300px' }} />
      </div>
      <p className="text-muted" style={{ fontSize: '0.85rem' }}>{units.length} unit(s)</p>
      <div style={{ overflowX: 'auto' }}>
        <table className="table" data-testid="fe-register-table">
          <thead><tr><th>Serial</th><th>Size</th><th>Status</th><th>Vehicle</th><th>Expiry</th>{canManage && <th></th>}</tr></thead>
          <tbody>
            {units.map(u => (
              <tr key={u.id}>
                <td className="font-mono">
                  {u.serial || '(no serial)'}
                  {u.cylinder_label && <div className="text-muted" style={{ fontSize: '0.75rem' }}>label {u.cylinder_label}</div>}
                </td>
                <td>{u.size}</td>
                <td><Pill map={UNIT_STATUS} value={u.status} /></td>
                <td>{u.vehicle_no || '–'}</td>
                <td style={{ color: u.expiry_status === 'expired' ? '#B91C1C' : u.expiry_status === 'due_soon' ? '#B45309' : undefined }}>
                  {fmtDate(u.expiry_date)}
                </td>
                {canManage && <td><button className="btn btn-sm btn-ghost" onClick={() => setEditing({ ...u })}>Edit</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Correct unit details</h3>
              <button className="modal-close" onClick={() => setEditing(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service tag (RW…)</label>
                <input className="form-input" value={editing.serial || ''} onChange={e => setEditing({ ...editing, serial: e.target.value.toUpperCase() })} />
              </div>
              <div className="form-group">
                <label className="form-label">Size</label>
                <select className="form-select" value={editing.size} onChange={e => setEditing({ ...editing, size: e.target.value })}>
                  {SIZES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Expiry</label>
                <input type="date" className="form-input" value={editing.expiry_date || ''} onChange={e => setEditing({ ...editing, expiry_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Cylinder label (PLS / SETSCO – stays with the cylinder)</label>
                <input className="form-input" value={editing.cylinder_label || ''} onChange={e => setEditing({ ...editing, cylinder_label: e.target.value.toUpperCase() })} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-input" value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== VENDORS & COSTS =====

const money = (v) => (v === null || v === undefined ? '–' : `$${Number(v).toFixed(2)}`);
const GST = 0.09;

function InvoiceFields({ inv, setInv }) {
  const gst = inv.gst_amount !== '' ? inv.gst_amount : (inv.amount_before_gst !== '' ? (Number(inv.amount_before_gst) * GST).toFixed(2) : '');
  return (
    <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
      <div className="form-group"><label className="form-label">Invoice no.</label>
        <input className="form-input" value={inv.invoice_no} onChange={e => setInv({ ...inv, invoice_no: e.target.value })} /></div>
      <div className="form-group"><label className="form-label">Invoice date</label>
        <input type="date" className="form-input" value={inv.invoice_date} onChange={e => setInv({ ...inv, invoice_date: e.target.value })} /></div>
      <div className="form-group"><label className="form-label">Amount before GST ($)</label>
        <input type="number" min="0" step="0.01" className="form-input" value={inv.amount_before_gst} data-testid="fe-invoice-amount"
          onChange={e => setInv({ ...inv, amount_before_gst: e.target.value })} /></div>
      <div className="form-group"><label className="form-label">GST ($, 9% if blank)</label>
        <input type="number" min="0" step="0.01" className="form-input" value={inv.gst_amount} placeholder={gst}
          onChange={e => setInv({ ...inv, gst_amount: e.target.value })} /></div>
    </div>
  );
}

const emptyInvoice = () => ({ invoice_no: '', invoice_date: '', amount_before_gst: '', gst_amount: '' });
const invoicePayload = (inv) => ({
  invoice_no: inv.invoice_no || null, invoice_date: inv.invoice_date || null,
  amount_before_gst: inv.amount_before_gst === '' ? null : Number(inv.amount_before_gst),
  gst_amount: inv.gst_amount === '' ? null : Number(inv.gst_amount)
});

function VendorsTab({ api, headers, jsonHeaders, canManage, validityMonths, setMessage, onDone }) {
  const [vendors, setVendors] = useState([]);
  const [batches, setBatches] = useState([]);
  const [receiving, setReceiving] = useState(null);
  const [invoicing, setInvoicing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, b] = await Promise.all([api('/api/fe/vendors', { headers }), api('/api/fe/batches', { headers })]);
      setVendors(v); setBatches(b);
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = (text) => { setMessage({ type: 'success', text }); setReceiving(null); setInvoicing(false); setEditing(null); load(); onDone(); };

  const cancelBatch = async (b) => {
    if (!window.confirm(`Cancel ${b.batch_no}? Its units go back to the returned / in-stock list. Only do this if the batch was never sent.`)) return;
    try {
      await api(`/api/fe/batches/${b.id}/cancel`, { method: 'POST', headers: jsonHeaders });
      done(`${b.batch_no} cancelled - its units are back where they were.`);
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="fe-batches">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Vendor batches</h3>
          {canManage && (
            <button className="btn btn-secondary btn-sm" onClick={() => setInvoicing(true)} data-testid="fe-invoice-for-stock-btn">
              Record an invoice for units already in stock
            </button>
          )}
        </div>
        <p className="text-muted" style={{ fontSize: '0.85rem' }}>
          Send returned units from the <strong>Stock</strong> tab (tick them, then “Send selected to vendor”). When they come back,
          press <strong>Receive back</strong> here and enter the invoice – each unit then carries its cost, which goes to the
          department/project of the vehicle it is fitted to.
        </p>
        {batches.length === 0 ? <p className="text-muted">No batches yet.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Batch</th><th>Vendor</th><th>Sent</th><th>Units</th><th>Received</th><th>Invoice</th><th>Before GST</th><th>Total</th><th></th></tr></thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 600 }}>{b.batch_no}{b.reference && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{b.reference}</div>}</td>
                    <td>{b.vendor_name}</td>
                    <td>{b.invoice_only ? '–' : fmtDate(b.sent_date)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{Object.entries(b.sizes || {}).map(([s, n]) => `${n} × ${s}`).join(', ')}</td>
                    <td>{b.status === 'at_vendor' ? <Pill map={UNIT_STATUS} value="at_vendor" />
                      : b.status === 'cancelled' ? <span className="text-muted">Cancelled</span> : fmtDate(b.received_date)}</td>
                    <td>{b.invoice_no || '–'}</td>
                    <td>{money(b.amount_before_gst)}</td>
                    <td>{money(b.total)}</td>
                    <td>
                      {b.status === 'at_vendor' && canManage
                        ? <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <button className="btn btn-primary btn-sm" onClick={() => setReceiving(b)} data-testid={`fe-receive-${b.batch_no}`}>Receive back</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => cancelBatch(b)} data-testid={`fe-cancel-${b.batch_no}`}>Cancel</button>
                          </div>
                        : <button className="btn btn-ghost btn-sm" onClick={() => setViewing(b)}>Details</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" data-testid="fe-vendors">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>FE vendors</h3>
          {canManage && <button className="btn btn-primary btn-sm" onClick={() => setEditing({})} data-testid="fe-add-vendor">+ Add vendor</button>}
        </div>
        <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
          <table className="table">
            <thead><tr><th>Vendor</th><th>Navision</th><th>Contact</th><th>Price list (before GST)</th>{canManage && <th></th>}</tr></thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.id}>
                  <td style={{ fontWeight: 600 }}>{v.name}{v.address && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{v.address}</div>}</td>
                  <td>{v.navision_no || '–'}</td>
                  <td style={{ fontSize: '0.85rem' }}>{[v.contact_name, v.phone, v.email].filter(Boolean).join(' · ') || '–'}</td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {Object.keys(v.prices || {}).length === 0 ? <span className="text-muted">none – invoices are shared equally per unit</span>
                      : Object.entries(v.prices).map(([size, p]) => `${size}: ${p.service !== undefined ? `service ${money(p.service)}` : ''}${p.new !== undefined ? ` new ${money(p.new)}` : ''}`).join(' · ')}
                    {v.control_tag_price ? ` · tag ${money(v.control_tag_price)}` : ''}
                  </td>
                  {canManage && <td><button className="btn btn-ghost btn-sm" onClick={() => setEditing(v)}>Edit</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {receiving && <ReceiveBatchModal api={api} headers={headers} jsonHeaders={jsonHeaders} batch={receiving} validityMonths={validityMonths}
        onClose={() => setReceiving(null)}
        onSaved={b => done(`${b.batch_no} received: ${(b.lines || []).filter(l => l.outcome !== 'condemned').length} unit(s) into stock, invoice ${money(b.amount_before_gst)} before GST shared across them.`)} />}
      {invoicing && <InvoiceForStockModal api={api} headers={headers} jsonHeaders={jsonHeaders} vendors={vendors}
        onClose={() => setInvoicing(false)}
        onSaved={b => done(`${b.batch_no}: invoice ${money(b.amount_before_gst)} before GST recorded for ${b.unit_count} unit(s).`)} />}
      {editing && <VendorModal api={api} jsonHeaders={jsonHeaders} vendor={editing} onClose={() => setEditing(null)}
        onSaved={v => done(`${v.name} saved.`)} />}
      {viewing && <BatchDetailsModal api={api} headers={headers} batch={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

function ReceiveBatchModal({ api, headers, jsonHeaders, batch, validityMonths, onClose, onSaved }) {
  const [units, setUnits] = useState([]);
  const [received, setReceived] = useState(today());
  const [rows, setRows] = useState({});
  const [newUnits, setNewUnits] = useState('');
  const [inv, setInv] = useState(emptyInvoice());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api(`/api/fe/batches/${batch.id}`, { headers }).then(b => {
      setUnits(b.units);
      setRows(Object.fromEntries(b.units.map(u => [u.id, { outcome: 'revalidated', new_serial: u.serial || '' }])));
    }).catch(err => setError(err.message));
  }, []);

  const newLines = newUnits.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
    const [serial, size] = l.split(/[,\t]/).map(x => x.trim());
    return { serial, size: (size || '9kg').toLowerCase().replace(/\s/g, '') };
  });
  const expiry = addMonths(received, validityMonths);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      onSaved(await api(`/api/fe/batches/${batch.id}/receive`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({
        received_date: received, ...invoicePayload(inv),
        units: units.map(u => ({ unit_id: u.id, outcome: rows[u.id].outcome, new_serial: rows[u.id].new_serial || null, new_expiry: expiry })),
        new_units: newLines.map(n => ({ ...n, expiry_date: expiry }))
      }) }));
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '760px' }} data-testid="fe-receive-modal">
        <div className="modal-header">
          <h3 className="modal-title">Receive {batch.batch_no} from {batch.vendor_name}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group" style={{ maxWidth: '220px' }}>
            <label className="form-label">Date received</label>
            <input type="date" className="form-input" value={received} max={today()} onChange={e => setReceived(e.target.value)} />
            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Revalidated units expire {fmtDate(expiry)}.</div>
          </div>
          <h4>Units in this batch ({units.length})</h4>
          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
            <table className="table">
              <thead><tr><th>Sent as</th><th>Size</th><th>Outcome</th><th>Service tag now (new tag if changed)</th></tr></thead>
              <tbody>
                {units.map(u => (
                  <tr key={u.id}>
                    <td className="font-mono">{u.serial || '(no tag)'}</td>
                    <td>{u.size}</td>
                    <td>
                      <select className="form-select" value={rows[u.id]?.outcome || 'revalidated'}
                        onChange={e => setRows({ ...rows, [u.id]: { ...rows[u.id], outcome: e.target.value } })}>
                        <option value="revalidated">Revalidated</option>
                        <option value="condemned">Condemned</option>
                      </select>
                    </td>
                    <td>
                      {rows[u.id]?.outcome === 'revalidated' && (
                        <input className="form-input" value={rows[u.id]?.new_serial || ''} placeholder="RW…"
                          onChange={e => setRows({ ...rows, [u.id]: { ...rows[u.id], new_serial: e.target.value.toUpperCase() } })} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label className="form-label">New units bought on the same invoice – one tag per line (add “, 1kg” for other sizes)</label>
            <textarea className="form-textarea" rows={3} value={newUnits} onChange={e => setNewUnits(e.target.value)} placeholder={'RW8769001\nRW8769002, 2kg'} />
          </div>
          <h4>Invoice</h4>
          <InvoiceFields inv={inv} setInv={setInv} />
          <p className="text-muted" style={{ fontSize: '0.8rem' }}>
            The amount before GST is shared across the revalidated and new units (by the vendor's price list if it has one,
            otherwise equally). If the invoice hasn't arrived yet, leave it blank and add it later with “Record an invoice for units already in stock”.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || units.length === 0} data-testid="fe-receive-save">
            {saving ? 'Saving…' : 'Receive batch'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceForStockModal({ api, headers, jsonHeaders, vendors, onClose, onSaved }) {
  const [units, setUnits] = useState([]);
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState('');
  const [vendorId, setVendorId] = useState(vendors[0]?.id || '');
  const [reference, setReference] = useState('');
  const [inv, setInv] = useState(emptyInvoice());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/api/fe/units?status=in_stock,fitted', { headers })
      .then(u => setUnits(u.filter(x => x.last_cost === undefined || x.last_cost === null)))
      .catch(err => setError(err.message));
  }, []);

  const shown = units.filter(u => !search || (u.serial || '').includes(search.toUpperCase()) || (u.revalidated_date || '').includes(search));
  const save = async () => {
    setSaving(true); setError(null);
    try {
      onSaved(await api('/api/fe/batches/invoice-for-stock', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({
        vendor_id: vendorId, unit_ids: picked, reference: reference || null, ...invoicePayload(inv) }) }));
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px' }} data-testid="fe-invoice-stock-modal">
        <div className="modal-header">
          <h3 className="modal-title">Invoice for units already in stock</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            For units that came back from a vendor before batches were used (e.g. the October 2026 batch). Their cost is
            also applied to the vehicles they have already been fitted to.
          </p>
          <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
            <div className="form-group"><label className="form-label">Vendor</label>
              <select className="form-select" value={vendorId} onChange={e => setVendorId(e.target.value)}>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">DO / job reference (optional)</label>
              <input className="form-input" value={reference} onChange={e => setReference(e.target.value)} /></div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="form-input" placeholder="Filter by tag or revalidated date (e.g. RW87655, 2026-10-23)" value={search}
              onChange={e => setSearch(e.target.value)} style={{ maxWidth: '360px' }} data-testid="fe-invoice-filter" />
            <button className="btn btn-sm btn-secondary" onClick={() => setPicked([...new Set([...picked, ...shown.map(u => u.id)])])} data-testid="fe-invoice-pick-all">
              Select all {shown.length} shown
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setPicked([])}>Clear</button>
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>{picked.length} selected</span>
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto', marginTop: '0.5rem' }}>
            <table className="table">
              <thead><tr><th></th><th>Tag</th><th>Size</th><th>Status</th><th>Revalidated</th></tr></thead>
              <tbody>
                {shown.map(u => (
                  <tr key={u.id}>
                    <td><input type="checkbox" checked={picked.includes(u.id)}
                      onChange={e => setPicked(e.target.checked ? [...picked, u.id] : picked.filter(x => x !== u.id))} /></td>
                    <td className="font-mono">{u.serial}</td><td>{u.size}</td>
                    <td><Pill map={UNIT_STATUS} value={u.status} />{u.vehicle_no && <span style={{ fontSize: '0.75rem' }}> {u.vehicle_no}</span>}</td>
                    <td>{fmtDate(u.revalidated_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4>Invoice</h4>
          <InvoiceFields inv={inv} setInv={setInv} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} data-testid="fe-invoice-stock-save"
            disabled={saving || picked.length === 0 || inv.amount_before_gst === '' || !vendorId}>
            {saving ? 'Saving…' : `Record invoice for ${picked.length} unit(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function VendorModal({ api, jsonHeaders, vendor, onClose, onSaved }) {
  const [v, setV] = useState({ name: '', navision_no: '', contact_name: '', phone: '', email: '', address: '', notes: '',
    prices: {}, control_tag_price: '', active: true, ...vendor });
  const [error, setError] = useState(null);
  const setPrice = (size, kind, value) => setV({ ...v, prices: { ...v.prices, [size]: { ...(v.prices[size] || {}), [kind]: value } } });

  const save = async () => {
    setError(null);
    const prices = {};
    Object.entries(v.prices || {}).forEach(([size, p]) => {
      const row = {};
      ['service', 'new'].forEach(k => { if (p[k] !== '' && p[k] !== undefined && p[k] !== null) row[k] = Number(p[k]); });
      if (Object.keys(row).length) prices[size] = row;
    });
    const body = { ...v, prices, control_tag_price: v.control_tag_price === '' || v.control_tag_price === null ? null : Number(v.control_tag_price) };
    try {
      onSaved(await api(vendor.id ? `/api/fe/vendors/${vendor.id}` : '/api/fe/vendors',
        { method: vendor.id ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(body) }));
    } catch (err) { setError(err.message); }
  };

  const field = (key, label) => (
    <div className="form-group"><label className="form-label">{label}</label>
      <input className="form-input" value={v[key] || ''} onChange={e => setV({ ...v, [key]: e.target.value })} /></div>
  );
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '620px' }} data-testid="fe-vendor-modal">
        <div className="modal-header">
          <h3 className="modal-title">{vendor.id ? `Edit ${vendor.name}` : 'Add FE vendor'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div style={{ display: 'grid', gap: '0 0.75rem', gridTemplateColumns: '1fr 1fr' }}>
            {field('name', 'Vendor name')}{field('navision_no', 'Navision vendor no. (e.g. PA048)')}
            {field('contact_name', 'Contact person')}{field('phone', 'Phone')}
            {field('email', 'Email')}{field('address', 'Address')}
          </div>
          {field('notes', 'Notes')}
          <h4>Price list, before GST (optional)</h4>
          <p className="text-muted" style={{ fontSize: '0.8rem' }}>Used to share an invoice fairly between sizes. Leave blank to share it equally per unit.</p>
          <table className="table">
            <thead><tr><th>Size</th><th>Servicing / revalidation ($)</th><th>New unit ($)</th></tr></thead>
            <tbody>
              {SIZES.map(size => (
                <tr key={size}>
                  <td>{size}</td>
                  {['service', 'new'].map(kind => (
                    <td key={kind}><input type="number" min="0" step="0.01" className="form-input" style={{ maxWidth: '120px' }}
                      value={v.prices?.[size]?.[kind] ?? ''} onChange={e => setPrice(size, kind, e.target.value)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="form-group" style={{ maxWidth: '200px' }}><label className="form-label">Control tag ($ per unit)</label>
            <input type="number" min="0" step="0.01" className="form-input" value={v.control_tag_price ?? ''}
              onChange={e => setV({ ...v, control_tag_price: e.target.value })} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!v.name.trim()} data-testid="fe-vendor-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function BatchDetailsModal({ api, headers, batch, onClose }) {
  const [b, setB] = useState(batch);
  useEffect(() => { api(`/api/fe/batches/${batch.id}`, { headers }).then(setB).catch(() => {}); }, []);
  const OUTCOME = { revalidated: 'Revalidated', condemned: 'Condemned', new: 'New unit bought', already_in_stock: 'Already in stock' };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }}>
        <div className="modal-header">
          <h3 className="modal-title">{b.batch_no} – {b.vendor_name}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '0.9rem' }}>
            {b.invoice_only ? 'Invoice for units already in stock' : `Sent ${fmtDate(b.sent_date)}`}
            {b.received_date && ` · received ${fmtDate(b.received_date)}`}{b.reference && ` · ref ${b.reference}`}<br />
            Invoice {b.invoice_no || '–'} {b.invoice_date && `(${fmtDate(b.invoice_date)})`}: {money(b.amount_before_gst)} + GST {money(b.gst_amount)} = <strong>{money(b.total)}</strong>
          </p>
          {(b.lines || []).length > 0 ? (
            <table className="table">
              <thead><tr><th>Tag</th><th>Size</th><th>Outcome</th><th>Cost before GST</th></tr></thead>
              <tbody>{b.lines.map(l => (
                <tr key={l.unit_id}><td className="font-mono">{l.serial}{l.previous_serial && <div className="text-muted" style={{ fontSize: '0.75rem' }}>was {l.previous_serial}</div>}</td>
                  <td>{l.size}</td><td>{OUTCOME[l.outcome] || l.outcome}</td><td>{money(l.cost)}</td></tr>
              ))}</tbody>
            </table>
          ) : <p className="text-muted">{(b.units || []).length} unit(s) at the vendor.</p>}
        </div>
      </div>
    </div>
  );
}

// ===== REMINDERS =====

const LEVEL_LABEL = (level) => {
  if (level.startsWith('overdue')) return 'Overdue';
  const d = level.split('-')[1];
  return d === '0' ? 'Expires today' : `${d}-day reminder`;
};

function RemindersTab({ api, headers, jsonHeaders, setMessage }) {
  const [settings, setSettings] = useState(null);
  const [daysText, setDaysText] = useState('');
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, p, l] = await Promise.all([
        api('/api/fe/reminders/settings', { headers }),
        api('/api/fe/reminders/preview', { headers }),
        api('/api/fe/reminders/log?limit=50', { headers })
      ]);
      setSettings(s); setDaysText(s.reminder_days.join(', ')); setPreview(p); setLog(l);
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (changes) => {
    setBusy('save');
    try {
      const s = await api('/api/fe/reminders/settings', { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(changes) });
      setSettings(s); setDaysText(s.reminder_days.join(', '));
      setMessage({ type: 'success', text: 'Reminder settings saved.' });
      load();
    } catch (err) { setMessage({ type: 'error', text: err.message }); } finally { setBusy(null); }
  };

  const sendNow = async () => {
    if (!window.confirm("Send today's reminders now? Anything already sent today is skipped.")) return;
    setBusy('send');
    try {
      const r = await api('/api/fe/reminders/run', { method: 'POST', headers });
      const skipped = r.skipped_no_contact.length ? ` No contact set for: ${r.skipped_no_contact.join(', ')}.` : '';
      setMessage({ type: 'success', text: `Sent ${r.vehicle_notices} vehicle notice(s)${r.digest ? ', the daily summary' : ''}${r.weekly ? ', the weekly summary' : ''}.${skipped}` });
      load();
    } catch (err) { setMessage({ type: 'error', text: err.message }); } finally { setBusy(null); }
  };

  if (!settings || !preview) return <div className="card"><p className="text-muted">Loading…</p></div>;
  const pending = preview.vehicle_notices.filter(n => !n.already_sent);

  return (
    <>
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="fe-reminder-settings">
        <h3 style={{ marginTop: 0 }}>Reminder settings</h3>
        <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', fontWeight: 600 }}>
          <input type="checkbox" checked={settings.reminders_enabled} disabled={busy === 'save'} data-testid="fe-reminders-enabled"
            onChange={e => save({ reminders_enabled: e.target.checked })} />
          Send reminders automatically every day at {String(settings.send_hour).padStart(2, '0')}:00
        </label>
        <p className="text-muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 1rem 1.6rem' }}>
          Switch this on in the <strong>live</strong> app only – preview has its own data, and its reminders would go to real people.
        </p>
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
          <div className="form-group">
            <label className="form-label">Remind this many days before expiry</label>
            <input className="form-input" value={daysText} onChange={e => setDaysText(e.target.value)} placeholder="30, 14, 7, 0" />
          </div>
          <div className="form-group">
            <label className="form-label">After expiry, remind every (days)</label>
            <input type="number" min="1" className="form-input" value={settings.overdue_repeat_days}
              onChange={e => setSettings({ ...settings, overdue_repeat_days: parseInt(e.target.value || '7', 10) })} />
          </div>
          <div className="form-group">
            <label className="form-label">Suggest swapping at a service up to (days before expiry)</label>
            <input type="number" min="0" className="form-input" value={settings.service_window_days}
              onChange={e => setSettings({ ...settings, service_window_days: parseInt(e.target.value || '0', 10) })} />
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" disabled={busy === 'save'} onClick={() => save({
          reminder_days: daysText.split(/[ ,]+/).filter(Boolean).map(Number),
          overdue_repeat_days: settings.overdue_repeat_days,
          service_window_days: settings.service_window_days
        })}>Save settings</button>
        <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '1rem' }}>
          Who receives the daily and weekly summaries, and whether by email or Telegram, is set under <strong>Dashboard → Notification Settings</strong>{' '}
          (“Fire Extinguishers” category). Vehicle notices go to the vehicle's contact from <strong>Servicing → Contacts</strong>;
          they get Telegram too if they have linked it under <strong>My Telegram</strong>.
        </p>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="fe-reminder-preview">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Today ({fmtDate(preview.date)})</h3>
          <button className="btn btn-primary" onClick={sendNow} disabled={busy === 'send'} data-testid="fe-reminders-send">
            {busy === 'send' ? 'Sending…' : 'Send now'}
          </button>
        </div>
        <p className="text-muted" style={{ fontSize: '0.85rem' }}>Nothing is sent from this list until you press Send now (or automatic sending is on).</p>

        <h4>Vehicle notices ({pending.length} to send{preview.vehicle_notices.length > pending.length ? `, ${preview.vehicle_notices.length - pending.length} already sent` : ''})</h4>
        {preview.vehicle_notices.length === 0 ? <p className="text-muted">No vehicle is due a reminder today.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Vehicle</th><th>FE expires</th><th>Reminder</th><th>Goes to</th><th>Servicing tip</th></tr></thead>
              <tbody>
                {preview.vehicle_notices.map(n => (
                  <tr key={n.key} style={{ opacity: n.already_sent ? 0.5 : 1 }}>
                    <td style={{ fontWeight: 600 }}>{n.vehicle_no}</td>
                    <td>{fmtDate(n.expiry_date)}<div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {n.days < 0 ? `${-n.days} days ago` : n.days === 0 ? 'today' : `in ${n.days} days`}</div></td>
                    <td>{LEVEL_LABEL(n.level)}{n.already_sent && <div className="text-muted" style={{ fontSize: '0.75rem' }}>sent</div>}</td>
                    <td>{n.contact
                      ? <>{n.contact.name || n.contact.email}<div className="text-muted" style={{ fontSize: '0.75rem' }}>email{n.contact.telegram_chat_id ? ' + Telegram' : ''}</div></>
                      : <span className="text-error">No contact – add one under Servicing → Contacts</span>}</td>
                    <td style={{ fontSize: '0.85rem' }}>{n.service_tip || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h4 style={{ marginTop: '1rem' }}>Daily summary for managers</h4>
        <p style={{ fontSize: '0.9rem' }}>
          {preview.digest.already_sent ? 'Already sent today.' : preview.digest.send ? 'Will be sent today:' : 'Nothing to report today:'}
          {' '}{preview.digest.overdue.length} overdue, {preview.digest.due.length} due soon,
          {' '}{preview.digest.low_stock.length} size(s) low on stock, {preview.digest.waiting_for_vendor} unit(s) waiting for the vendor.
          <br /><span className="text-muted">To: {preview.digest.recipients.map(r => r.name || r.email).join(', ') || 'nobody – set recipients in Notification Settings'}</span>
        </p>
        <h4>Weekly summary for management</h4>
        <p style={{ fontSize: '0.9rem' }}>
          Sent on Mondays{preview.weekly.send ? (preview.weekly.already_sent ? ' – already sent this week.' : ' – due today.') : '.'}
          <br /><span className="text-muted">To: {preview.weekly.recipients.map(r => r.name || r.email).join(', ') || 'nobody'}</span>
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Recently sent</h3>
        {log.length === 0 ? <p className="text-muted">Nothing sent yet.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>When</th><th>What</th><th>To</th><th>Result</th></tr></thead>
              <tbody>
                {log.map(l => (
                  <tr key={l.key}>
                    <td>{fmtDate(l.date)}</td>
                    <td>{l.kind === 'vehicle' ? `Vehicle notice – ${l.vehicle_no}` : l.kind === 'digest' ? 'Daily summary' : 'Weekly summary'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{l.to || '–'}<div className="text-muted" style={{ fontSize: '0.75rem' }}>{[...new Set((l.results || []).map(r => r.channel))].join(' + ')}</div></td>
                    <td>{(l.results || []).length === 0 ? '–' : (l.results.every(r => r.success)
                      ? <span className="text-success">Sent</span>
                      : <span className="text-error" title={l.results.filter(r => !r.success).map(r => r.message).join('; ')}>Failed – {l.results.filter(r => !r.success).map(r => r.channel).join(', ')}</span>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ===== REPORTS =====

function ReportsTab({ headers, setMessage, canSeeCosts }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(null);

  const download = async (key, path) => {
    setBusy(key);
    try {
      const res = await fetch(`${BACKEND_URL}${path}`, { headers });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const name = (res.headers.get('Content-Disposition') || '').split('filename=')[1] || `fe_${key}_${today()}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name.replace(/"/g, '');
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const range = new URLSearchParams();
  if (from) range.set('date_from', from);
  if (to) range.set('date_to', to);

  const reports = [
    { key: 'register', title: 'FE Register', desc: 'Every unit: serial, size, status, vehicle, expiry.', path: '/api/fe/reports/register' },
    { key: 'vehicles', title: 'FE by Vehicle', desc: 'FE status of every vehicle and the serials on board.', path: '/api/fe/reports/vehicles' },
    { key: 'exchanges', title: 'Exchange Log', desc: 'Every unit fitted or taken off, with department and project at the time – uses the dates above.', path: `/api/fe/reports/exchanges?${range}` },
    { key: 'stock', title: 'Stock', desc: 'Units in stock, fitted, returned and at vendor, by size.', path: '/api/fe/reports/stock' },
    ...(canSeeCosts ? [
      { key: 'costs', title: 'Cost by Department / Project', desc: 'Cost (before GST) of every unit fitted, by department, project and vehicle – uses the dates above.', path: `/api/fe/reports/costs?${range}` },
      { key: 'vendor-spend', title: 'Vendor Batches & Spend', desc: 'Every batch sent to a vendor, with its invoice – uses the dates above.', path: `/api/fe/reports/vendor-spend?${range}` }
    ] : [])
  ];

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Download reports (Excel)</h3>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div className="form-group">
          <label className="form-label">From (Exchange Log{canSeeCosts ? ', costs, vendor spend' : ''})</label>
          <input type="date" className="form-input" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">to</label>
          <input type="date" className="form-input" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {reports.map(r => (
          <div key={r.key} style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '1rem', background: '#FAFBFC' }}>
            <h4 style={{ margin: '0 0 0.25rem', color: '#003366' }}>{r.title}</h4>
            <p className="text-muted" style={{ fontSize: '0.85rem', minHeight: '2.5rem' }}>{r.desc}</p>
            <button className="btn btn-primary btn-sm" onClick={() => download(r.key, r.path)} disabled={busy === r.key}
              data-testid={`fe-report-${r.key}`}>
              {busy === r.key ? 'Preparing…' : '⬇ Download'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FireExtinguishers;
